/**
 * SyllabusEditor — admin-only widget shown on each topic/point row.
 *
 * Capabilities:
 *  • Edit title inline (pencil → input → save)
 *  • Move up / move down among siblings (reorder)
 *  • Move to a different parent (within the same organ tree)
 *
 * Lifts state out of OrganPage via callbacks so parent can refetch syllabus
 * after every successful change.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Edit3, ArrowUp, ArrowDown, Check, X, FolderInput, Loader2, FileText } from "lucide-react";

export function SyllabusEditor({ node, organTree, apiUrl, onChanged, lang = "ms", compact = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(node.title || "");
  const [busy, setBusy] = useState(false);
  const [showMove, setShowMove] = useState(false);
  const [editContent, setEditContent] = useState(false);
  const [contentDraft, setContentDraft] = useState("");
  const [loadingContent, setLoadingContent] = useState(false);

  const t = (en, ms) => (lang === "en" ? en : ms);

  const save = async (payload) => {
    setBusy(true);
    try {
      await axios.put(`${apiUrl}/nodes/${node.id}`, payload);
      onChanged && onChanged();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Save failed", "Gagal simpan"));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const submitTitle = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const newTitle = (draft || "").trim();
    if (!newTitle || newTitle === node.title) {
      setEditing(false);
      return;
    }
    if (await save({ title: newTitle })) {
      toast.success(t("Title updated", "Tajuk dikemaskini"));
      setEditing(false);
    }
  };

  const moveDir = async (dir) => {
    const ok = await save({ order: (node.order || 0) + (dir === "up" ? -1 : 1) });
    if (ok) toast.success(t("Order updated", "Urutan ditukar"));
  };

  const moveToParent = async (newParentId) => {
    setShowMove(false);
    if (!newParentId || newParentId === node.parent_id) return;
    if (await save({ parent_id: newParentId })) {
      toast.success(t("Moved", "Telah dialih"));
    }
  };

  const openContentEditor = async () => {
    setLoadingContent(true);
    setEditContent(true);
    try {
      const res = await axios.get(`${apiUrl}/nodes/${node.id}/content`);
      const notes = (res.data?.notes && res.data.notes[node.id]) || "";
      setContentDraft(notes);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Failed to load notes", "Gagal muat nota"));
      setEditContent(false);
    } finally {
      setLoadingContent(false);
    }
  };

  const saveContent = async () => {
    if (await save({ notes: contentDraft })) {
      toast.success(t("Notes updated", "Nota dikemaskini"));
      setEditContent(false);
    }
  };

  // Build flat list of candidate parents from the organ tree (exclude self & descendants)
  const buildParentOptions = () => {
    const out = [];
    const isDescendant = (n) => {
      if (!n) return false;
      if (n.id === node.id) return true;
      return (n.children || []).some(isDescendant);
    };
    const walk = (n, depth) => {
      if (!n) return;
      if (!isDescendant(n) && n.id !== node.parent_id) {
        out.push({ id: n.id, label: "  ".repeat(depth) + (n.title || "(no title)") });
      }
      (n.children || []).forEach((c) => walk(c, depth + 1));
    };
    walk(organTree, 0);
    return out;
  };

  if (editing) {
    return (
      <form
        onSubmit={submitTitle}
        onClick={(e) => e.stopPropagation()}
        className="flex items-center gap-1.5 flex-1 min-w-0"
        data-testid="syllabus-edit-form"
      >
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") { setEditing(false); setDraft(node.title || ""); }
          }}
          className="flex-1 min-w-0 px-2 py-1 text-sm font-semibold text-gray-800 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-300"
          data-testid="syllabus-edit-input"
        />
        <button type="submit" disabled={busy} className="p-1 rounded hover:bg-green-50 text-green-600 disabled:opacity-50" data-testid="syllabus-edit-save">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
        </button>
        <button type="button" onClick={(e) => { e.stopPropagation(); setEditing(false); setDraft(node.title || ""); }} className="p-1 rounded hover:bg-red-50 text-red-500" data-testid="syllabus-edit-cancel">
          <X size={14} />
        </button>
      </form>
    );
  }

  const parentOptions = showMove ? buildParentOptions() : [];

  return (
    <div
      className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity"
      onClick={(e) => e.stopPropagation()}
      data-testid="syllabus-edit-toolbar"
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        title={t("Rename", "Tukar tajuk")}
        className="p-1 rounded hover:bg-blue-50 text-blue-500"
        data-testid={`syllabus-rename-${node.id}`}
      >
        <Edit3 size={compact ? 12 : 13} />
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); openContentEditor(); }}
        title={t("Edit notes/content", "Edit kandungan/nota")}
        className="p-1 rounded hover:bg-emerald-50 text-emerald-500"
        data-testid={`syllabus-edit-content-${node.id}`}
      >
        <FileText size={compact ? 12 : 13} />
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); moveDir("up"); }}
        title={t("Move up", "Naikkan")}
        disabled={busy}
        className="p-1 rounded hover:bg-gray-100 text-gray-500 disabled:opacity-40"
        data-testid={`syllabus-up-${node.id}`}
      >
        <ArrowUp size={compact ? 12 : 13} />
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); moveDir("down"); }}
        title={t("Move down", "Turunkan")}
        disabled={busy}
        className="p-1 rounded hover:bg-gray-100 text-gray-500 disabled:opacity-40"
        data-testid={`syllabus-down-${node.id}`}
      >
        <ArrowDown size={compact ? 12 : 13} />
      </button>
      <div className="relative">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setShowMove(!showMove); }}
          title={t("Move to…", "Anjak ke…")}
          className="p-1 rounded hover:bg-purple-50 text-purple-500"
          data-testid={`syllabus-move-${node.id}`}
        >
          <FolderInput size={compact ? 12 : 13} />
        </button>
        {showMove && (
          <div
            className="absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg w-64 max-h-64 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="syllabus-move-dropdown"
          >
            <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-gray-400 border-b border-gray-100">
              {t("Move under", "Anjak ke bawah")}
            </div>
            {parentOptions.length === 0 ? (
              <div className="px-3 py-3 text-xs text-gray-400">{t("No other parent available", "Tiada parent lain")}</div>
            ) : (
              parentOptions.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => moveToParent(p.id)}
                  className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-blue-50 hover:text-blue-600 whitespace-pre"
                  data-testid={`syllabus-move-to-${p.id}`}
                >
                  {p.label}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {editContent && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          onClick={() => { if (!busy) setEditContent(false); }}
          data-testid="content-editor-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-100">
              <div className="min-w-0">
                <h3 className="text-base font-bold text-slate-900 truncate" title={node.title}>
                  {t("Edit Notes", "Sunting Nota")}: {node.title}
                </h3>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  {t("Plain prose recommended. Avoid #headings or **bold** markdown.", "Tulis prosa biasa. Elakkan #tajuk atau **bold** markdown.")}
                </p>
              </div>
              <button
                onClick={() => { if (!busy) setEditContent(false); }}
                className="p-1.5 rounded-full hover:bg-slate-100 text-slate-500"
                data-testid="content-editor-close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {loadingContent ? (
                <div className="flex items-center justify-center py-12 text-slate-400">
                  <Loader2 size={20} className="animate-spin mr-2" /> {t("Loading…", "Memuatkan…")}
                </div>
              ) : (
                <textarea
                  value={contentDraft}
                  onChange={(e) => setContentDraft(e.target.value)}
                  rows={20}
                  className="w-full p-4 text-sm text-slate-800 leading-relaxed border border-slate-200 rounded-lg font-mono focus:outline-none focus:ring-2 focus:ring-blue-200"
                  placeholder={t("Write the topic content here…", "Tulis kandungan topik di sini…")}
                  data-testid="content-editor-textarea"
                />
              )}
            </div>
            <div className="flex items-center justify-between gap-2 p-4 border-t border-slate-100 bg-slate-50/50 rounded-b-2xl">
              <span className="text-[11px] text-slate-500">
                {contentDraft.length} {t("characters", "aksara")}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setEditContent(false)}
                  disabled={busy}
                  className="px-4 py-2 text-sm font-semibold text-slate-600 rounded-full hover:bg-slate-100 disabled:opacity-50"
                  data-testid="content-editor-cancel"
                >
                  {t("Cancel", "Batal")}
                </button>
                <button
                  onClick={saveContent}
                  disabled={busy || loadingContent}
                  className="px-5 py-2 text-sm font-bold text-white bg-emerald-600 rounded-full hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
                  data-testid="content-editor-save"
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                  {busy ? t("Saving…", "Menyimpan…") : t("Save", "Simpan")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SyllabusEditor;
